<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  profile: { type: Object, default: null },
  mission: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  showRules: { type: Boolean, default: true },
  showMission: { type: Boolean, default: true },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['rules', 'rewards', 'mission']);
const { t } = useI18n();

const points = computed(() => Number(props.profile?.points || 2450));
const level = computed(() => Math.max(1, Number(props.profile?.level || 8)));
const levelProgress = computed(() =>
  Math.min(100, Number(props.profile?.level_progress || 72))
);
const levelProgressLabel = computed(() =>
  t('GAMIFICATION.BAR.LEVEL_PROGRESS', {
    progress: Math.round(levelProgress.value),
  })
);
const levelLabel = computed(
  () => props.profile?.level_label || `Nível ${level.value} · Especialista`
);
const streak = computed(() => Number(props.profile?.streak_days || 12));
const missionPoints = computed(() => Number(props.mission?.points || 50));
const missionProgress = computed(() => Number(props.mission?.progress || 1));
const missionTarget = computed(() => Number(props.mission?.target || 2));
const missionLabel = computed(() => props.mission?.title || 'Missão do dia');
const missionDescription = computed(
  () => props.mission?.description || 'Conclua uma etapa e ganhe pontos'
);
const missionProgressLabel = computed(() => {
  if (!missionTarget.value) return '';
  return `${missionProgress.value}/${missionTarget.value}`;
});
const missionPointsLabel = computed(() =>
  t('GAMIFICATION.BAR.MISSION_POINTS', {
    points: missionPoints.value,
    unit: t('GAMIFICATION.BAR.POINTS_SHORT'),
  })
);
</script>

<template>
  <section
    class="flex min-w-0 items-stretch overflow-x-auto rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
    :class="compact ? 'min-h-[64px] gap-0' : 'min-h-[72px] gap-1'"
    :aria-busy="loading ? 'true' : 'false'"
    :aria-label="t('GAMIFICATION.BAR.ARIA_LABEL')"
  >
    <div class="flex min-w-[190px] items-center gap-3 px-4 py-3">
      <div
        class="grid size-10 shrink-0 place-items-center rounded-xl bg-[#5b24e6] text-white shadow-lais-soft"
        aria-hidden="true"
      >
        <span class="i-lucide-shield-check size-5" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center justify-between gap-2">
          <p class="truncate text-[12px] font-semibold text-lais-text">
            {{ levelLabel }}
          </p>
          <span class="text-[11px] font-medium text-lais-text-muted">
            {{ levelProgressLabel }}
          </span>
        </div>
        <progress
          class="mt-2 h-1.5 w-full overflow-hidden rounded-full [appearance:none] [&::-moz-progress-bar]:bg-lais-primary [&::-webkit-progress-bar]:bg-lais-primary-50 [&::-webkit-progress-value]:bg-lais-primary"
          :value="levelProgress"
          max="100"
        />
      </div>
    </div>

    <div
      class="flex min-w-[132px] items-center gap-2 border-l border-lais-border-soft px-4 py-3"
    >
      <span
        class="grid size-8 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
        aria-hidden="true"
      >
        <span class="i-lucide-flame size-4" />
      </span>
      <div>
        <p class="text-[11px] text-lais-text-muted">Sequência</p>
        <p class="text-sm font-bold text-lais-text">{{ streak }} dias</p>
      </div>
    </div>

    <div
      class="flex min-w-[140px] items-center gap-2 border-l border-lais-border-soft px-4 py-3"
    >
      <span
        class="grid size-8 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
        aria-hidden="true"
      >
        <span class="i-lucide-sparkles size-4" />
      </span>
      <div>
        <p class="text-[11px] text-lais-text-muted">Pontos</p>
        <p class="text-sm font-bold text-lais-text">
          {{ points.toLocaleString('pt-BR') }}
        </p>
      </div>
    </div>

    <button
      type="button"
      class="flex min-w-[126px] items-center gap-2 border-l border-lais-border-soft px-4 py-3 text-left transition hover:bg-lais-primary-50/30 focus-visible:outline-none focus-visible:shadow-lais-focus"
      @click="emit('rewards')"
    >
      <span
        class="grid size-8 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
        aria-hidden="true"
      >
        <span class="i-lucide-gift size-4" />
      </span>
      <span class="text-xs font-semibold text-lais-primary">Prêmios</span>
    </button>

    <button
      v-if="showMission"
      type="button"
      class="flex min-w-[250px] flex-1 items-center gap-3 border-l border-lais-border-soft bg-lais-primary-50/50 px-4 py-3 text-left transition hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
      @click="emit('mission')"
    >
      <span
        class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
        aria-hidden="true"
      >
        <span class="i-lucide-target size-4" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-[11px] font-semibold text-lais-text">{{
          missionLabel
        }}</span>
        <span class="mt-0.5 block truncate text-[11px] text-lais-text-muted">{{
          missionDescription
        }}</span>
      </span>
      <span
        class="shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-bold text-lais-primary shadow-lais-soft"
      >
        {{ missionPointsLabel }}
        <span
          v-if="missionProgressLabel"
          class="ml-1 font-normal text-lais-text-muted"
        >
          {{ missionProgressLabel }}
        </span>
      </span>
    </button>

    <button
      v-if="showRules"
      type="button"
      class="flex min-w-[150px] items-center justify-center gap-2 border-l border-lais-border-soft px-4 py-3 text-xs font-semibold text-lais-text-body transition hover:bg-lais-bg-soft focus-visible:outline-none focus-visible:shadow-lais-focus"
      @click="emit('rules')"
    >
      <span
        class="i-lucide-circle-help size-4 text-lais-text-muted"
        aria-hidden="true"
      />
      Regras dos pontos
    </button>
  </section>
</template>
